// Client, deliberately, and it costs nothing: the seam puts a Client Component
// at every anchor either way, so the only question is WHICH props cross the RSC
// boundary. Marked client, a Server Component block sends this atom's four
// compact inputs; left server, it sends the fourteen expanded anchor attributes
// this atom computes from them. Measured across the estate (2026-08-11): client
// shrinks the flight payload 2.1% and the HTML users download 1.5%; server grows
// them 0.5% and 0.2%. Blocks stay Server Components regardless — a Server
// Component may render a Client one.
'use client';

import React from 'react';
// Anchors render through the link-renderer seam, never a bare <a> — see
// helpers/linkRenderer. This atom is the funnel for the whole chrome
// (TopNavLink, MainNavItem, SubNavItem, MenuItem, Breadcrumbs, Footer), so
// routing it here reaches all of them without any of them knowing.
import { Anchor } from '../../helpers/linkRenderer';

export interface LinkProps {
  /**
   * Link URL
   */
  href: string;
  /**
   * Link text/content
   */
  children: React.ReactNode;
  /**
   * Remove underline (links are underlined by default)
   */
  noUnderline?: boolean;
  /**
   * External link (opens in new tab)
   */
  external?: boolean;
  /**
   * Disabled state
   */
  disabled?: boolean;
  /**
   * Click event handler
   */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  /**
   * Target attribute
   */
  target?: '_blank' | '_self' | '_parent' | '_top';
  /**
   * Rel attribute
   */
  rel?: string;
  /**
   * HTML title attribute
   */
  title?: string;
  /**
   * HTML id attribute
   */
  id?: string;
  /**
   * Custom CSS classes
   */
  className?: string;
  /**
   * Inline styles
   */
  style?: React.CSSProperties;
  /**
   * ARIA role
   */
  role?: string;
  /**
   * Tab index for keyboard navigation
   */
  tabIndex?: number;
  /**
   * ARIA label
   */
  'aria-label'?: string;
  /**
   * ARIA described by
   */
  'aria-describedby'?: string;
  /**
   * ARIA current (for navigation)
   */
  'aria-current'?: 'page' | 'step' | 'location' | 'date' | 'time' | 'true' | 'false';
}

export const Link: React.FC<LinkProps> = ({
  href,
  children,
  noUnderline = false,
  external = false,
  disabled = false,
  onClick,
  target,
  rel,
  title,
  id,
  className = '',
  style,
  role,
  tabIndex,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedby,
  'aria-current': ariaCurrent,
}) => {
  // Base class - includes underline by default
  const baseClass = 'hi-ui-link hi-ui-link-underline';

  // No underline class
  const noUnderlineClass = noUnderline ? 'hi-ui-link-no-underline' : '';

  // Disabled class
  const disabledClass = disabled ? 'hi-ui-link-disabled' : '';

  const allClasses = `${baseClass} ${noUnderlineClass} ${disabledClass} ${className}`.trim();

  // Handle external links
  const linkTarget = external ? '_blank' : target;
  const linkRel = external ? (rel ? `${rel} noopener noreferrer` : 'noopener noreferrer') : rel;

  // Only attach click handler when needed
  const handleClick =
    disabled || onClick
      ? (event: React.MouseEvent<HTMLAnchorElement>) => {
          if (disabled) {
            event.preventDefault();
            return;
          }
          onClick!(event);
        }
      : undefined;

  return (
    <Anchor
      href={disabled ? undefined : href}
      className={allClasses}
      onClick={handleClick}
      target={linkTarget}
      rel={linkRel}
      title={title}
      id={id}
      style={style}
      role={role}
      tabIndex={tabIndex !== undefined ? tabIndex : disabled ? -1 : undefined}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedby}
      aria-current={ariaCurrent}
      aria-disabled={disabled ? true : undefined}
    >
      {children}
    </Anchor>
  );
};
